import React, { useState } from 'react';
import { Alert, Box, Link, Paper, Stack, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Navigate, useLocation } from 'react-router-dom';

import ApplicationLoading from '../../../../common/ApplicationLoading';
import { Button } from '../../../../components/core';
import CopyButton from '../../../../components/core/CopyButton';
import { agentHubPaths } from '../../../../util/constants';
import type { BigCommerceOnboardingStep } from '../../../../store/modules/merchant/types';
import { toast } from '../../../../util/toast';
import { StatusChip } from '../../../ShopifyProvisioning/StatusChip';
import {
  BIGCOMMERCE_APP_INSTALL_URL,
  buttonPaddingSx,
  getApiErrorMessage,
  getMerchantOwnedSteps,
  getMerchantProvisioningStatus,
  getStatusLabel,
  getStepLabel,
  MERCHANT_OWNED_STEP_KEYS,
  ONBOARDING_STATUS_COLORS,
} from './utils';
import { useProvisioningPlatform } from '../useProvisioningPlatform';
import { useBigCommerceProvisioningEligibility } from './useBigCommerceProvisioningEligibility';

type StepCardProps = {
  number: number;
  title: string;
  status: BigCommerceOnboardingStep['status'];
  children: React.ReactNode;
};

const StepStatus = ({ status }: { status: BigCommerceOnboardingStep['status'] }) => (
  <StatusChip label={getStatusLabel(status)} color={ONBOARDING_STATUS_COLORS[status]} />
);

const StepCard = ({ number, title, status, children }: StepCardProps) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography component="h2" fontWeight="semibold">
          {number}. {title}
        </Typography>
        <Box sx={{ flex: 1, borderBottom: 1, borderColor: 'divider' }} />
        <StepStatus status={status} />
      </Stack>
      <Stack spacing={2}>{children}</Stack>
    </Stack>
  </Paper>
);

const actionUrlSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  minWidth: 0,
  fontFamily: 'Poppins, sans-serif',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.4,
  color: 'link.main',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  wordBreak: 'break-all',
} as const;

const ActionUrl = ({ url, copyTitle }: { url: string; copyTitle: string }): JSX.Element => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
    <Link href={url} target="_blank" rel="noreferrer" sx={actionUrlSx}>
      {url}
      <OpenInNew sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden />
    </Link>
    <CopyButton copyString={url} title={copyTitle} />
  </Stack>
);

export const ProvisioningBigCommercePage = (): JSX.Element => {
  const { search } = useLocation();
  const { platform, isLoading: isPlatformLoading } = useProvisioningPlatform();
  const {
    onboarding,
    isLoading: isEligibilityLoading,
    isOnboardingError,
    refetchOnboarding,
  } = useBigCommerceProvisioningEligibility();
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  if (isPlatformLoading || isEligibilityLoading) {
    return <ApplicationLoading />;
  }

  if (platform !== 'bigcommerce') {
    return <Navigate to={`${agentHubPaths.DASHBOARD}${search}`} replace />;
  }

  if (isOnboardingError || !onboarding) {
    return (
      <Box data-testid="ella-provisioning-bigcommerce-page">
        <Alert severity="info">
          BigCommerce provisioning has not started for this store yet. Finish Agent Hub onboarding
          to create your setup checklist.
        </Alert>
      </Box>
    );
  }

  const provisioningStatus = getMerchantProvisioningStatus(onboarding);
  const merchantSteps = getMerchantOwnedSteps(onboarding);
  const stepNumber = (key: BigCommerceOnboardingStep['key']) =>
    MERCHANT_OWNED_STEP_KEYS.indexOf(key) + 1;

  const checkStatus = async () => {
    setIsRefreshingStatus(true);
    try {
      await refetchOnboarding().unwrap();
      toast.success('Onboarding status refreshed.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to refresh onboarding status.'));
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  return (
    <Box data-testid="ella-provisioning-bigcommerce-page">
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <StatusChip label={provisioningStatus.label} color={provisioningStatus.color} />
          </Stack>
          <Button
            text="Check Status"
            sx={buttonPaddingSx}
            onClick={() => {
              void checkStatus();
            }}
            disabled={isRefreshingStatus}
            loading={isRefreshingStatus}
          />
        </Stack>

        <Stack spacing={3}>
          {merchantSteps.map((step) => {
            if (step.key === 'oauth_complete') {
              return (
                <StepCard
                  key={step.key}
                  number={stepNumber(step.key)}
                  title="Install Skipify in BigCommerce"
                  status={step.status}
                >
                  <Stack spacing={1.25} data-testid="ella-provisioning-install-url">
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      Open your BigCommerce app marketplace, install Skipify, and approve the
                      requested permissions. After you install the app, click{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        Check Status
                      </Box>{' '}
                      above to confirm it connected.
                    </Typography>
                    <ActionUrl
                      url={BIGCOMMERCE_APP_INSTALL_URL}
                      copyTitle="BigCommerce install link"
                    />
                  </Stack>
                </StepCard>
              );
            }

            if (step.key === 'scripts_injected') {
              return (
                <StepCard
                  key={step.key}
                  number={stepNumber(step.key)}
                  title={getStepLabel(step.key)}
                  status={step.status}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    Skipify injects the checkout script automatically during app installation. Click{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Check Status
                    </Box>{' '}
                    above to confirm when this step is complete.
                  </Typography>
                </StepCard>
              );
            }

            return null;
          })}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Need help? Contact your Skipify representative if any step is blocked.
        </Typography>
      </Stack>
    </Box>
  );
};
